import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Shield, Calendar, Crown, Zap, ArrowLeft, Key, Plus, Copy, Trash2, Loader2, HardDrive, Plug } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import DriveConnectionPanel from './DriveConnectionPanel';
import BillingPanel from './BillingPanel';
import { supabaseHelpers } from '../lib/supabase';
import { withBackend } from '../utils/api';
import { checkConnection, connectDrive } from '../services/driveService';

const TIER_LABELS = {
  free: { label: 'Free', color: 'bg-muted text-muted-foreground', icon: User },
  starter: { label: 'Starter', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: Zap },
  // 'pro' is a legacy alias of Business on the backend (same limits).
  pro: { label: 'Business', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Crown },
  business: { label: 'Business', color: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400', icon: Crown },
  team: { label: 'Team', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400', icon: Crown },
  max: { label: 'Max', color: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400', icon: Crown },
  trial: { label: 'Trial', color: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400', icon: Zap },
  prezlab: { label: 'Prezlab', color: 'bg-gradient-to-r from-primary/20 to-accent/20 text-primary', icon: Crown },
};


/**
 * r113: the account identity card, lifted out of the page so the Settings
 * modal can render the SAME one. Account used to be a separate page while
 * everything else lived in Settings, so people had two places to look for
 * their own details. Exported rather than copied: two drifting copies of an
 * identity card is how a plan badge ends up disagreeing with itself.
 */
export function AccountSummary({ user, userName, usageData }) {
  const [memberSince, setMemberSince] = useState(null);
  useEffect(() => {
    if (user?.created_at) setMemberSince(new Date(user.created_at));
  }, [user]);
  const tier = usageData?.tier || 'free';
  const tierInfo = TIER_LABELS[tier] || TIER_LABELS.free;
  const TierIcon = tierInfo.icon;
  return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="rounded-xl border border-border bg-card p-6 mb-6"
      >
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
            {(userName || user?.email || '?')[0].toUpperCase()}
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-lg font-semibold text-foreground">{userName || 'User'}</h2>
              <Badge className={`text-xs font-medium px-2 py-0.5 ${tierInfo.color}`}>
                <TierIcon className="w-3 h-3 mr-1" />
                {tierInfo.label}
              </Badge>
            </div>

            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Mail className="w-3.5 h-3.5" />
                <span>{user?.email || '—'}</span>
              </div>
              {memberSince && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>Member since {memberSince.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</span>
                </div>
              )}
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Shield className="w-3.5 h-3.5" />
                <span>Signed in via {user?.app_metadata?.provider === 'google' ? 'Google' : 'Email'}</span>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
  );
}

export default function UserProfile({ user, userName, usageData, onBack }) {
  // Tier/plan rendering lives in <AccountSummary/> now — the page and the
  // Settings modal share it rather than each deriving its own badge.
  return (
    <div className="max-w-3xl mx-auto px-6 py-10">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </button>

        <h1 className="text-2xl font-bold text-foreground">Account</h1>
        <p className="text-sm text-muted-foreground mt-1">Manage your profile and view usage</p>
      </motion.div>

      {/* Profile Card */}
      <AccountSummary user={user} userName={userName} usageData={usageData} />

      {/* r113: the Monthly Usage quota grid lived here and was REMOVED.
          It showed per-model monthly allowances (0/40 Flash, 0/15 Pro...)
          from a quota system that credits replaced. Worse than stale: those
          quotas really were still enforced server-side, so it advertised a
          wall paying customers should never hit. Credits, spend-to-date and
          top-ups all render just below in <BillingPanel/>, which reads
          /api/billing/summary — one meter, one place. */}

      {/* Connected services — cloud storage for importing files */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="mt-8 rounded-2xl border border-border bg-card p-6"
      >
        <div className="flex items-center gap-2 mb-2">
          <HardDrive className="w-4 h-4 text-violet-500" />
          <h2 className="text-base font-semibold text-foreground">Connected services</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
          Connect Google Drive or OneDrive to import documents, sheets, and decks
          straight from the prompt box. Access is read-only.
        </p>
        <DriveConnectionPanel userId={user?.id} variant="page" />
      </motion.div>

      {/* Plan, credit balance, plan switching and top-ups. Renders nothing
          when billing is not configured on the backend. */}
      <BillingPanel userId={user?.id} email={user?.email} />

      {/* App connections — CRM/ERP integrations that feed generations. */}
      <AppConnections userId={user?.id} />

      {/* Personal Access Tokens — paste these into Claude Desktop / Cursor
          / any MCP-aware client to generate decks from inside the chat. */}
      <PersonalAccessTokens userId={user?.id} />
    </div>
  );
}


export function AppConnections({ userId }) {
  const [hubspot, setHubspot] = useState({ loading: true, connected: false, configured: true });
  const [odoo, setOdoo] = useState({ loading: true, connected: false });
  const [busy, setBusy] = useState(false);
  // Odoo is API-key based (no OAuth popup) — the row expands into a small
  // credentials form instead of opening a browser flow.
  const [odooFormOpen, setOdooFormOpen] = useState(false);
  const [odooForm, setOdooForm] = useState({ url: '', db: '', login: '', api_key: '' });

  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      const status = await checkConnection(userId, 'hubspot');
      setHubspot({ loading: false, ...status });
    } catch {
      setHubspot({ loading: false, connected: false, configured: true });
    }
    try {
      const status = await checkConnection(userId, 'odoo');
      setOdoo({ loading: false, ...status });
    } catch {
      setOdoo({ loading: false, connected: false });
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const connect = async () => {
    setBusy(true);
    try {
      await connectDrive(userId, 'hubspot'); // provider-generic OAuth popup helper
      toast.success('HubSpot connected');
      refresh();
    } catch (err) {
      toast.error(err.message || 'Connection failed');
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm('Disconnect HubSpot? Deal-based generation will stop working.')) return;
    setBusy(true);
    try {
      await fetch(withBackend('/api/oauth/hubspot/disconnect'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      toast.success('HubSpot disconnected');
      refresh();
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  const connectOdoo = async () => {
    const { url, db, login, api_key } = odooForm;
    if (!url.trim() || !db.trim() || !login.trim() || !api_key.trim()) {
      toast.error('All four fields are required');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(withBackend('/api/odoo/connect'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, ...odooForm }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Connection failed');
      toast.success('Odoo connected');
      setOdooFormOpen(false);
      setOdooForm({ url: '', db: '', login: '', api_key: '' });
      refresh();
    } catch (err) {
      toast.error(err.message || 'Connection failed');
    } finally {
      setBusy(false);
    }
  };

  const disconnectOdoo = async () => {
    if (!window.confirm('Disconnect Odoo? Opportunity/project-based generation will stop working.')) return;
    setBusy(true);
    try {
      await fetch(withBackend('/api/oauth/odoo/disconnect'), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      toast.success('Odoo disconnected');
      refresh();
    } catch {
      toast.error('Failed to disconnect');
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.18 }}
      className="mt-8 rounded-2xl border border-border bg-card p-6"
    >
      <div className="flex items-center gap-2 mb-2">
        <Plug className="w-4 h-4 text-violet-500" />
        <h2 className="text-base font-semibold text-foreground">App connections</h2>
      </div>
      <p className="text-xs text-muted-foreground mb-4 leading-relaxed">
        Connect your business tools so generations can pull live data — pick a HubSpot
        deal and get a proposal deck built around it.
      </p>

      <div className="space-y-2">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-border">
          <span className="w-8 h-8 rounded-lg bg-orange-500/10 text-orange-600 flex items-center justify-center text-xs font-bold">HS</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-foreground">HubSpot</p>
            <p className="text-xs text-muted-foreground truncate">
              {hubspot.loading ? 'Checking…'
                : hubspot.connected ? `Connected${hubspot.account_name ? ` · ${hubspot.account_name}` : ''}`
                : hubspot.configured === false ? 'Not configured on this server yet'
                : 'Generate proposal decks from your deals'}
            </p>
          </div>
          {hubspot.connected ? (
            <Button variant="outline" size="sm" onClick={disconnect} disabled={busy}>
              Disconnect
            </Button>
          ) : (
            <Button size="sm" onClick={connect} disabled={busy || hubspot.loading || hubspot.configured === false}>
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect'}
            </Button>
          )}
        </div>

        <div className="rounded-md border border-border">
          <div className="flex items-center gap-3 px-3 py-2.5">
            <span className="w-8 h-8 rounded-lg bg-purple-500/10 text-purple-600 flex items-center justify-center text-xs font-bold">Od</span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Odoo</p>
              <p className="text-xs text-muted-foreground truncate">
                {odoo.loading ? 'Checking…'
                  : odoo.connected ? `Connected${odoo.account_name ? ` · ${odoo.account_name}` : ''}`
                  : 'Generate decks and reports from your opportunities and projects'}
              </p>
            </div>
            {odoo.connected ? (
              <Button variant="outline" size="sm" onClick={disconnectOdoo} disabled={busy}>
                Disconnect
              </Button>
            ) : (
              <Button size="sm" onClick={() => setOdooFormOpen(v => !v)} disabled={busy || odoo.loading}>
                {odooFormOpen ? 'Cancel' : 'Connect'}
              </Button>
            )}
          </div>
          {odooFormOpen && !odoo.connected && (
            <div className="border-t border-border/60 px-3 py-3 space-y-2">
              <p className="text-[11px] text-muted-foreground leading-relaxed">
                Create an API key in Odoo under My Profile → Account Security → API Keys,
                then enter your instance details below.
              </p>
              <input
                type="url"
                value={odooForm.url}
                onChange={(e) => setOdooForm(f => ({ ...f, url: e.target.value }))}
                placeholder="Instance URL — e.g. https://mycompany.odoo.com"
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
              <div className="flex gap-2">
                <input
                  type="text"
                  value={odooForm.db}
                  onChange={(e) => setOdooForm(f => ({ ...f, db: e.target.value }))}
                  placeholder="Database name"
                  className="flex-1 min-w-0 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
                <input
                  type="email"
                  value={odooForm.login}
                  onChange={(e) => setOdooForm(f => ({ ...f, login: e.target.value }))}
                  placeholder="Login email"
                  className="flex-1 min-w-0 rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-purple-400"
                />
              </div>
              <input
                type="password"
                value={odooForm.api_key}
                onChange={(e) => setOdooForm(f => ({ ...f, api_key: e.target.value }))}
                placeholder="API key"
                autoComplete="off"
                className="w-full rounded-md border border-border bg-background px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-1 focus:ring-purple-400"
              />
              <Button size="sm" onClick={connectOdoo} disabled={busy} className="w-full">
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Connect Odoo'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
}


export function PersonalAccessTokens({ userId }) {
  const [tokens, setTokens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newTokenName, setNewTokenName] = useState('');
  // When we generate a token the plaintext is shown ONCE in this state
  // and then cleared. Persisting it anywhere would defeat the security
  // model (only the hash lives in the DB).
  const [justCreatedToken, setJustCreatedToken] = useState(null);

  const refresh = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const resp = await fetch(withBackend(`/api/pat?user_id=${encodeURIComponent(userId)}`));
      const data = await resp.json();
      setTokens(data.tokens || []);
    } catch (e) {
      toast.error('Failed to load API tokens');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = async () => {
    if (!userId) return;
    const name = newTokenName.trim() || 'Untitled token';
    setCreating(true);
    try {
      const resp = await fetch(withBackend('/api/pat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, name }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setJustCreatedToken({ token: data.token, name: data.name });
      setNewTokenName('');
      refresh();
    } catch (e) {
      toast.error('Failed to create token');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId) => {
    if (!userId) return;
    if (!window.confirm('Revoke this token? Anything using it will stop working immediately.')) return;
    try {
      const resp = await fetch(withBackend(`/api/pat/${tokenId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId }),
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      toast.success('Token revoked');
      refresh();
    } catch (e) {
      toast.error('Failed to revoke token');
    }
  };

  const handleCopy = (token) => {
    navigator.clipboard.writeText(token).then(
      () => toast.success('Token copied to clipboard'),
      () => toast.error('Copy failed — select the text manually'),
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      className="mt-8 rounded-2xl border border-border bg-card p-6"
    >
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Key className="w-4 h-4 text-violet-500" />
          <h2 className="text-base font-semibold text-foreground">API tokens</h2>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mb-5 leading-relaxed">
        Generate a token to connect Wizardly to Claude Desktop, Cursor, or any other
        MCP-aware client. Tokens authenticate as your account and share your monthly
        usage quota. Anyone with a token can generate on your behalf — treat them like passwords.
      </p>

      {/* Newly created plaintext — shown once. */}
      {justCreatedToken && (
        <div className="mb-5 rounded-lg border border-violet-500/30 bg-violet-500/5 p-4">
          <div className="flex items-start gap-2 mb-2">
            <Shield className="w-4 h-4 text-violet-500 mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground">Copy your new token now</p>
              <p className="text-xs text-muted-foreground mt-1">
                This is the only time we'll show the full token. If you lose it, generate a new one.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <code className="flex-1 min-w-0 truncate text-xs font-mono px-3 py-2 rounded-md bg-muted text-foreground">
              {justCreatedToken.token}
            </code>
            <Button size="sm" variant="outline" onClick={() => handleCopy(justCreatedToken.token)}>
              <Copy className="w-3.5 h-3.5 mr-1" />
              Copy
            </Button>
          </div>
          <button
            type="button"
            onClick={() => setJustCreatedToken(null)}
            className="mt-3 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
          >
            I've copied it — dismiss
          </button>
        </div>
      )}

      {/* Generate form */}
      <div className="flex items-center gap-2 mb-5">
        <input
          type="text"
          value={newTokenName}
          onChange={e => setNewTokenName(e.target.value)}
          placeholder="Token name (e.g. Claude Desktop)"
          className="flex-1 min-w-0 px-3 py-2 text-sm rounded-md border border-border bg-background focus:outline-none focus:ring-2 focus:ring-violet-500/40 focus:border-violet-500"
          disabled={creating}
        />
        <Button size="sm" onClick={handleCreate} disabled={creating}>
          {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />}
          Generate
        </Button>
      </div>

      {/* Token list */}
      {loading ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground py-4">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          Loading tokens…
        </div>
      ) : tokens.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 text-center">
          No tokens yet. Generate one to connect Wizardly to your AI assistant.
        </p>
      ) : (
        <div className="space-y-2">
          {tokens.map(tk => (
            <div key={tk.id} className="flex items-center gap-3 px-3 py-2.5 rounded-md border border-border bg-background">
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-foreground truncate">{tk.name}</div>
                <div className="text-[11px] text-muted-foreground flex items-center gap-2 mt-0.5">
                  <code className="font-mono">{tk.token_prefix}…</code>
                  <span>·</span>
                  <span>Created {new Date(tk.created_at).toLocaleDateString()}</span>
                  {tk.last_used_at && (
                    <>
                      <span>·</span>
                      <span>Last used {new Date(tk.last_used_at).toLocaleDateString()}</span>
                    </>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleRevoke(tk.id)}
                className="p-2 rounded-md text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors"
                aria-label="Revoke token"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
